import React from 'react';
import { useStepActions } from '../../store/stepStore';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('NodeIcon');

// Import static icons as fallbacks
import TableInputIcon from '../../assets/icons/tableinput.svg?react';
import TableOutputIcon from '../../assets/icons/tableoutput.svg?react';
import FilterRowsIcon from '../../assets/icons/filterrows.svg?react';

// Type definitions
interface NodeIconProps {
  type?: string;
  nodeType?: string;
  className?: string;
  style?: React.CSSProperties;
}

type IconComponent = React.ComponentType<{ className?: string; style?: React.CSSProperties }>;

// Create a mapping from node type string to the icon component
const nodeTypeToIconMap: Record<string, IconComponent> = {
  TableInput: TableInputIcon,
  tableOutput: TableOutputIcon,
  filterRows: FilterRowsIcon,
};

// Dynamic Icon Component that uses step data from the store
const NodeIcon: React.FC<NodeIconProps> = ({ type, nodeType, className, style }) => {
  const { getStepIcon, getStepIconByNodeType } = useStepActions();

  // Debug logging
  // logger.debug('NodeIcon props:', { type, nodeType });

  // Handle "unknown" type specially - only if the step type itself is unknown
  if (type === 'unknown') {
    logger.debug('Unknown step type detected, using QuestionCircleOutlined');
    return <QuestionCircleOutlined className={className} style={style} />;
  }

  // Try to get icon by type first (this is the step type for icon lookup)
  const iconSvg = type ? getStepIcon(type) : null;
  // logger.debug('Icon by type:', type, iconSvg ? 'found' : 'not found');

  // Fallback to static icons if not found in steps store
  const StaticIcon = type ? nodeTypeToIconMap[type] : null;
  // logger.debug('Static icon:', StaticIcon ? 'found' : 'not found');

  if (iconSvg) {
    // Render SVG from backend
    return (
      <div className={className} style={style} dangerouslySetInnerHTML={{ __html: iconSvg }} />
    );
  } else if (StaticIcon) {
    // Fallback to static icon
    return <StaticIcon className={className} style={style} />;
  } else {
    // Default fallback to QuestionCircleOutlined
    return <QuestionCircleOutlined className={className} style={style} />;
  }
};

export default NodeIcon;
